import axios from "axios";
import { Feature, ImageTile, Map } from "ol";
import { Tile } from "ol/layer";
import TileLayer from "ol/layer/Tile";
import TileState from "ol/TileState";
import { XYZ } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import { Stroke, Style } from "ol/style";
import { AttributeType } from "@/geoMap/define/AttributeType";
import { FeatureGeoJson } from "./../FeatureGeoJson";
import { ILayerFactory } from "./ILayerFactory";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { FeatureType } from "../define/FeatureType";
import { ServerError } from "../define/ServerError";
import { DRMLinkAttributeConvert } from "@/attribute/convert/DRMLinkAttributeConvert";
import { LayerUtil } from "../util/LayerUtil"

/**
 * 対象FeatureType
 */
const FEATURE_TYPE = FeatureType.DrmLink;

/**
 * 基本道路 コンテキストメニュー名
 */
const CONTEXT_MENU_BASIC = "DRM-DB_基本道路_リンク";

/**
 * 全道路 コンテキストメニュー名
 */
const CONTEXT_MENU_ALL = "DRM-DB_全道路_リンク";

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 8;

/**
 * ズームレベル対応トラランス
 */

const TOLERANCE_BY_ZOOMLEVEL = [
  {
    zoomLevel: 18,
    tolerance: 5,
  },
  {
    zoomLevel: 17,
    tolerance: 50,
  },
  {
    zoomLevel: 16,
    tolerance: 50,
  },
  {
    zoomLevel: 15,
    tolerance: 50,
  },
  {
    zoomLevel: 14,
    tolerance: 50,
  },
  {
    zoomLevel: 13,
    tolerance: 50,
  },
  {
    zoomLevel: 12,
    tolerance: 100,
  },
  {
    zoomLevel: 11,
    tolerance: 100,
  },
  {
    zoomLevel: 10,
    tolerance: 200,
  },
  {
    zoomLevel: 9,
    tolerance: 200,
  },
];

/**
 * DRMリンク道路種別 型定義
 */
type DrmLinkRoadType = 1 | 2;

/**
 * DRMリンクレイヤクラス
 */
export class DrmLinkMapLayerFactory implements ILayerFactory {
  /**
   * Authorizationヘッダに与えるIDトークンを設定する
   * @param idToken Authorizationヘッダに与える値
   */
  static setIdToken(_idToken: string) {
    return;
  }
  /**
   * 道路種別
   */
  private _roadType: DrmLinkRoadType;

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return false;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * DRMリンクレイヤ
   */
  private _layer: Tile<XYZ>;

  /**
   * DRMノードソース
   */
  private _tileSource: XYZ;

  constructor() {
    this._tileSource = new XYZ();
    this._tileSource.setTileLoadFunction(async function (tile, src) {
      const imageTile = tile as ImageTile;
      const response = await axios
        .get(src, {
          headers: {
            custom: "viewer",
          },
          responseType: "blob",
        })
        .catch(() => {
          tile.setState(TileState.ERROR);
        });
      if (response) {
        const image = imageTile.getImage() as HTMLImageElement;
        image.src = URL.createObjectURL(response.data);
      } else {
        tile.setState(TileState.ERROR);
      }
    });
    this._layer = new Tile({
      source: this._tileSource,
      minZoom: LAYER_MIN_ZOOMLEVEL,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
    });
  }

  /**
   * レイヤ定義を取得
   */
  getViewLayer(): TileLayer<XYZ>[] {
    return [this._layer];
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    return this._layer?.getVisible() ? 1 : 0;
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    map: Map,
    latitude: number,
    longitude: number
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // API取得メソッド（APIGateWayが整い次第実装）
    const tolerance = TOLERANCE_BY_ZOOMLEVEL.find((obj) => {
      return obj.zoomLevel === Math.round(map.getView().getZoom());
    }).tolerance;
    const params = {
      request_crs: "EPSG:4326",
      response_crs: "EPSG:3857",
      road_classification: this._roadType,
      point_y: longitude.toString(),
      point_x: latitude.toString(),
      tolerance,
    };
    const url = process.env.VUE_APP_DRM_LINK_API_URL;
    const res = await axios
      .get(url, {
        params,
        headers: {
          custom: "viewer",
        },
      })
      .catch((error) => {
        throw new ServerError(
          url,
          error.response?.status && "0",
          error.response?.message
        );
      });
    if (!res.data?.geo_data) return null;
    const parseResData = JSON.parse(res.data.geo_data);
    const selectFeatureInfoList: SelectedFeatureInfo[] = [];
    // GeoJSONに座標系が設定されていた場合、Feature生成時に指定
    const dataProjection = parseResData.crs?.properties?.name;
    const features = new GeoJSON().readFeatures(parseResData, {
      dataProjection,
      featureProjection: "EPSG:3857",
    });

    const style = new Style({
      stroke: new Stroke({
        color: "hotpink",
        width: 5,
      }),
    });

    features.forEach((feature) => {
      const geometry = feature.getGeometry();
      const selectFeature = new Feature({
        geometry,
      });

      // 内部識別用IDを生成、set
      const id = FEATURE_TYPE + "-" + feature.get("pid");
      selectFeature.setId(id);
      selectFeature.setStyle(style);
      const geoJSON = new GeoJSON().writeFeature(feature);
      const featureGeoJson = new FeatureGeoJson(geoJSON, id);
      const contextMenuName =
        this._roadType === 1 ? CONTEXT_MENU_BASIC : CONTEXT_MENU_ALL;
      const selectedFeatureInfo = new SelectedFeatureInfo(
        selectFeature,
        contextMenuName,
        featureGeoJson,
        AttributeType.Normal,
        new DRMLinkAttributeConvert(geoJSON)
      );
      selectFeatureInfoList.push(selectedFeatureInfo);
    });
    return selectFeatureInfoList;
  }

  /**
   * 右クリック処理
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getRightClickInfo(): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * 指定範囲の地物を取得
   * @param layerNames レイヤ名リスト
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    _map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      _map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      _map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }

    // bounding boxから矩形ポリゴンの外周線座標を生成する
    // Polygonの外周線は反時計回りで閉じている
    // Polygonのcoordinatesは外周線1つと任意数の内周線を持った配列からなる
    let [x1, y1, x2, y2] = bBox;
    if (x1 > x2) [x1, x2] = [x2, x1];
    if (y1 > y2) [y1, y2] = [y2, y1];
    const bBoxPolygonCoordinates = [
      [
        [x1, y1],
        [x1, y2],
        [x2, y2],
        [x2, y1],
        [x1, y1],
      ],
    ];

    const spatial_search_area = {
      type: "Polygon",
      coordinates: bBoxPolygonCoordinates,
    };
    const url = process.env.VUE_APP_DRM_CONSOLIDATE_API_URL;
    const { data } = await axios
      .get(url, {
        headers: {
          custom: "viewer",
        },
        params: {
          road_classification: this._roadType,
          request_crs: "EPSG:3857",
          response_crs: "EPSG:3857",
          spatial_search_erea: spatial_search_area,
        },
      })
      .catch((error) => {
        throw new ServerError(
          url,
          error.response?.status && "0",
          error.response?.message
        );
      });

    // 該当データがない場合、null
    if (!data.status) {
      return null;
    }

    const geoJson = data.geo_data_link;
    const features = new GeoJSON().readFeatures(geoJson);
    const selectedFeatureInfoList = features.map((feature) => {
      const selectFeature = new Feature({
        geometry: feature.getGeometry(),
      });
      const contextMenuName =
        this._roadType === 1 ? CONTEXT_MENU_BASIC : CONTEXT_MENU_ALL;
      const properties = feature.getProperties();
      const selectedGeoJson = new GeoJSON().writeFeature(feature);
      const featureGeoJson = new FeatureGeoJson(
        selectedGeoJson,
        properties["pid"]
      );
      const attributeType = AttributeType.Normal;
      const attributeConvert = new DRMLinkAttributeConvert(selectedGeoJson);
      return new SelectedFeatureInfo(
        selectFeature,
        contextMenuName,
        featureGeoJson,
        attributeType,
        attributeConvert
      );
    });
    return selectedFeatureInfoList;
  }

  /**
   * 表示状態を切り替え
   * @param isVisible 表示/非表示
   * @param roadType 道路種別
   */

  setVisible(isVisible: boolean, roadType?: DrmLinkRoadType): void {
    if (isVisible) {
      this._roadType = roadType;
      this._tileSource.setUrl(
        this._roadType === 2
          ? process.env.VUE_APP_DRM_LINK_PRV_XYZ_URL
          : process.env.VUE_APP_DRM_LINK_BASIC_XYZ_URL
      );
      this._tileSource.refresh();
    }
    this._layer.setVisible(isVisible);
  }
  /**
   * 表示レイヤが存在するか判定
   */
   isVisibleLayer(_map:Map) {
    return LayerUtil.isVisibleLayer(_map, this._layer)
  }
}
